import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useTheme } from '../contexts/AppContexts';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Textarea } from './ui/textarea';
import { ArrowLeft, Upload, Camera, X, FileText, FileSpreadsheet, FileType, Paperclip } from 'lucide-react';
import useImageDrop from '../utils/useImageDrop';
import { API_BASE_URL } from '../config';

const API = `${API_BASE_URL}/api`;

// Helper function to calculate overall rating
export const calculateOverallRatingHelper = (reportData) => {
  const assessmentAreas = [
    'command_and_control',
    'operations',
    'planning',
    'logistics',
    'finance_admin',
    'communication',
    'resource_management',
    'safety_and_security',
    'training_and_readiness'
  ];

  const ratingValues = {
    'Excellent': 5,
    'Above Average': 4,
    'Average': 3,
    'Below Average': 2,
    'Unacceptable': 1
    // 'Not Assessed' intentionally omitted — it is excluded from the average
  };

  const ratedAreas = assessmentAreas
    .map(area => reportData[area]?.rating)
    .filter(rating => rating && ratingValues[rating])
    .map(rating => ratingValues[rating]);

  if (ratedAreas.length === 0) {
    return 'Not Assessed';
  }

  const average = ratedAreas.reduce((sum, value) => sum + value, 0) / ratedAreas.length;

  if (average >= 4.5) return 'Excellent';
  if (average >= 3.5) return 'Above Average';
  if (average >= 2.5) return 'Average';
  if (average >= 1.5) return 'Below Average';
  return 'Unacceptable';
};

const EvaluationReportForm = ({ exerciseId, editingReport, onBack, onSave }) => {
  const { theme } = useTheme();

  // Build the initial formData from editingReport (if any) so the first render
  // already has the saved ratings. This avoids the Radix Select "locked on
  // first controlled value" race condition where a later setFormData in a
  // useEffect wouldn't propagate the updated rating to the Select trigger.
  const buildInitialFormData = (source) => {
    const src = source || {};
    const areaDefault = (key, name) => src[key] || { area_name: name, rating: 'Average', comments: '' };
    return {
      report_title: src.report_title || '',
      evaluator_name: src.evaluator_name || '',
      evaluator_organization: src.evaluator_organization || '',
      evaluation_date: src.evaluation_date || (source ? '' : new Date().toISOString().split('T')[0]),
      exercise_overview: src.exercise_overview || '',
      summary_of_findings: src.summary_of_findings || '',
      strengths: src.strengths || '',
      areas_for_improvement: src.areas_for_improvement || '',
      key_findings_narrative: src.key_findings_narrative || '',
      recommendations: src.recommendations || '',
      appendices: src.appendices || '',
      appendix_files: src.appendix_files || [],
      command_and_control: areaDefault('command_and_control', 'Command and Control'),
      operations: areaDefault('operations', 'Operations'),
      planning: areaDefault('planning', 'Planning'),
      logistics: areaDefault('logistics', 'Logistics'),
      finance_admin: areaDefault('finance_admin', 'Finance / Admin'),
      communication: areaDefault('communication', 'Communication'),
      resource_management: areaDefault('resource_management', 'Resource Management'),
      safety_and_security: areaDefault('safety_and_security', 'Safety and Security'),
      training_and_readiness: areaDefault('training_and_readiness', 'Training and Readiness'),
      evaluation_images: src.evaluation_images || []
    };
  };

  const [formData, setFormData] = useState(() => buildInitialFormData(editingReport));
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [imagePreview, setImagePreview] = useState(editingReport?.evaluation_images || []);

  const ratingOptions = ['Excellent', 'Above Average', 'Average', 'Below Average', 'Unacceptable', 'Not Assessed'];

  // If the parent swaps editingReport (e.g. edit a different report without
  // unmounting), re-hydrate. The parent already passes a `key` bound to the
  // report id so this is mostly a safety net.
  useEffect(() => {
    setFormData(buildInitialFormData(editingReport));
    setImagePreview(editingReport?.evaluation_images || []);
  }, [editingReport]);

  const validateForm = () => {
    const newErrors = {};
    if (!formData.report_title.trim()) {
      newErrors.report_title = 'Report title is required';
    }
    if (!formData.evaluator_name.trim()) {
      newErrors.evaluator_name = 'Evaluator name is required';
    }
    if (!formData.evaluation_date.trim()) {
      newErrors.evaluation_date = 'Evaluation date is required';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleAreaAssessmentChange = (areaKey, field, value) => {
    setFormData(prev => ({
      ...prev,
      [areaKey]: { ...prev[areaKey], [field]: value }
    }));
  };

  const calculateOverallRating = () => {
    return calculateOverallRatingHelper(formData);
  };

  const handleImageUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const imageData = e.target.result;
        setImagePreview(prev => [...prev, imageData]);
        setFormData(prev => ({ 
          ...prev, 
          evaluation_images: [...prev.evaluation_images, imageData] 
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const { dropProps: evalImageDropProps, isDragging: isEvalImageDragging } = useImageDrop({
    onFile: (file) => handleImageUpload({ target: { files: [file] } }),
    multiple: true
  });

  const handleCameraCapture = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true });
      const video = document.createElement('video');
      video.srcObject = stream;
      video.play();
      
      video.addEventListener('loadedmetadata', () => {
        const canvas = document.createElement('canvas');
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(video, 0, 0);
        
        const imageData = canvas.toDataURL('image/jpeg');
        setImagePreview(prev => [...prev, imageData]);
        setFormData(prev => ({ 
          ...prev, 
          evaluation_images: [...prev.evaluation_images, imageData] 
        }));
        
        stream.getTracks().forEach(track => track.stop());
      });
    } catch (error) {
      console.error('Error accessing camera:', error);
      alert('Unable to access camera. Please use file upload instead.');
    }
  };

  const removeImage = (index) => {
    setImagePreview(prev => prev.filter((_, i) => i !== index));
    setFormData(prev => ({ 
      ...prev, 
      evaluation_images: prev.evaluation_images.filter((_, i) => i !== index) 
    }));
  };

  // Appendix file upload (.doc/.docx, .xls/.xlsx, .pdf, .ppt/.pptx)
  const ALLOWED_APPENDIX_EXTS = ['.doc', '.docx', '.xls', '.xlsx', '.pdf', '.ppt', '.pptx'];
  const MAX_APPENDIX_BYTES = 10 * 1024 * 1024; // 10 MB

  const formatFileSize = (bytes) => {
    if (!bytes) return '0 B';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  };

  const getFileIcon = (filename) => {
    const ext = (filename || '').toLowerCase().split('.').pop();
    if (['xls', 'xlsx'].includes(ext)) return FileSpreadsheet;
    if (['doc', 'docx'].includes(ext)) return FileText;
    if (['ppt', 'pptx'].includes(ext)) return FileType;
    return Paperclip;
  };

  const handleAppendixUpload = (event) => {
    const files = Array.from(event.target.files || []);
    if (files.length === 0) return;

    files.forEach((file) => {
      const lower = (file.name || '').toLowerCase();
      const ok = ALLOWED_APPENDIX_EXTS.some(ext => lower.endsWith(ext));
      if (!ok) {
        alert(`"${file.name}" — unsupported type. Allowed: ${ALLOWED_APPENDIX_EXTS.join(', ')}`);
        return;
      }
      if (file.size > MAX_APPENDIX_BYTES) {
        alert(`"${file.name}" exceeds 10 MB limit (${formatFileSize(file.size)})`);
        return;
      }
      const reader = new FileReader();
      reader.onload = (e) => {
        const fileEntry = {
          id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
          name: file.name,
          type: file.type || '',
          size: file.size,
          data: e.target.result // base64 data URL
        };
        setFormData(prev => ({
          ...prev,
          appendix_files: [...(prev.appendix_files || []), fileEntry]
        }));
      };
      reader.onerror = () => alert(`Failed to read "${file.name}"`);
      reader.readAsDataURL(file);
    });

    // Reset input so the same file can be re-selected if removed
    event.target.value = '';
  };

  const removeAppendixFile = (index) => {
    setFormData(prev => ({
      ...prev,
      appendix_files: prev.appendix_files.filter((_, i) => i !== index)
    }));
  };

  const downloadAppendixFile = (file) => {
    try {
      const a = document.createElement('a');
      a.href = file.data;
      a.download = file.name || 'appendix';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } catch (e) {
      console.error('Download failed:', e);
    }
  };

  const handleSubmitLocal = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setLoading(true);
    
    try {
      const reportData = { ...formData, exercise_id: exerciseId };
      if (editingReport) {
        await axios.put(`${API}/evaluation-reports/${editingReport.id}`, reportData);
      } else {
        await axios.post(`${API}/evaluation-reports`, reportData);
      }
      onSave();
    } catch (error) {
      console.error('Error saving evaluation report:', error);
      alert('Failed to save evaluation report. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-5xl mx-auto p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-orange-500 mb-2">
            {editingReport ? 'Edit Evaluation Report' : 'Create Evaluation Report'}
          </h1>
          <p className={theme.colors.textMuted}>
            Comprehensive evaluation report for exercise performance assessment
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button 
            variant="outline" 
            onClick={onBack}
            className="text-orange-500 border-orange-500 hover:bg-orange-500 hover:text-black"
            data-testid="eval-form-back-btn"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Evaluations
          </Button>
          <Button
            type="submit"
            form="evaluation-report-form"
            disabled={loading}
            className="bg-orange-500 hover:bg-orange-600 text-black font-semibold px-6"
            data-testid="eval-form-submit-btn-top"
          >
            {loading ? 'Saving...' : (editingReport ? 'Update Report' : 'Create Report')}
          </Button>
        </div>
      </div>

      <form id="evaluation-report-form" onSubmit={handleSubmitLocal} className="space-y-6">
        {/* Basic Information */}
        <Card className={`${theme.colors.secondary} ${theme.colors.border}`}>
          <CardHeader>
            <CardTitle className="text-orange-500">Basic Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="report_title" className={theme.colors.textPrimary}>Report Title *</Label>
                <Input
                  id="report_title"
                  type="text"
                  value={formData.report_title}
                  onChange={(e) => handleInputChange('report_title', e.target.value)}
                  placeholder="Exercise Evaluation Report"
                  className={theme.colors.input}
                  data-testid="eval-report-title"
                />
                {errors.report_title && <p className="text-red-400 text-sm mt-1">{errors.report_title}</p>}
              </div>
              <div>
                <Label htmlFor="evaluation_date" className={theme.colors.textPrimary}>Evaluation Date *</Label>
                <Input
                  id="evaluation_date"
                  type="date"
                  value={formData.evaluation_date}
                  onChange={(e) => handleInputChange('evaluation_date', e.target.value)}
                  className={theme.colors.input}
                  data-testid="eval-date"
                />
                {errors.evaluation_date && <p className="text-red-400 text-sm mt-1">{errors.evaluation_date}</p>}
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="evaluator_name" className={theme.colors.textPrimary}>Evaluator Name *</Label>
                <Input
                  id="evaluator_name"
                  type="text"
                  value={formData.evaluator_name}
                  onChange={(e) => handleInputChange('evaluator_name', e.target.value)}
                  placeholder="Primary evaluator's name"
                  className={theme.colors.input}
                  data-testid="eval-evaluator-name"
                />
                {errors.evaluator_name && <p className="text-red-400 text-sm mt-1">{errors.evaluator_name}</p>}
              </div>
              <div>
                <Label htmlFor="evaluator_organization" className={theme.colors.textPrimary}>Evaluator Organization</Label>
                <Input
                  id="evaluator_organization"
                  type="text"
                  value={formData.evaluator_organization}
                  onChange={(e) => handleInputChange('evaluator_organization', e.target.value)}
                  placeholder="Organization or department"
                  className={theme.colors.input}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Supporting Images */}
        <Card className={`${theme.colors.secondary} ${theme.colors.border}`}>
          <CardHeader>
            <CardTitle className="text-orange-500">Supporting Images</CardTitle>
          </CardHeader>
          <CardContent>
            <div
              {...evalImageDropProps}
              data-testid="evaluation-images-dropzone"
              className={`space-y-4 rounded-lg border-2 border-dashed p-4 transition-colors ${
                isEvalImageDragging ? 'border-orange-500 bg-orange-500/10 ring-2 ring-orange-500' : 'border-gray-700'
              }`}
              title="Drop images anywhere in this area to upload"
            >
              <p className={`${theme.colors.textMuted} text-xs`}>
                Click a button below or <strong>drag &amp; drop</strong> images anywhere in this box
              </p>
              <div className="flex items-center space-x-4">
                <div>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    className="hidden"
                    id="evaluation-image-upload"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => document.getElementById('evaluation-image-upload').click()}
                    className="border-orange-500/50 text-orange-500 hover:bg-orange-500/10 mr-3"
                  >
                    <Upload className="h-4 w-4 mr-2" />
                    Upload Image
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleCameraCapture}
                    className="border-orange-500/50 text-orange-500 hover:bg-orange-500/10"
                  >
                    <Camera className="h-4 w-4 mr-2" />
                    Use Camera
                  </Button>
                </div>
              </div>
              {imagePreview.length > 0 && (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {imagePreview.map((image, index) => (
                    <div key={`eval-img-${index}-${image.slice(-10)}`} className="relative">
                      <img 
                        src={image} 
                        alt={`Evaluation ${index + 1}`}
                        className="w-full h-32 object-cover rounded-lg border border-gray-600"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="absolute top-1 right-1 text-red-400 border-red-400 hover:bg-red-400 hover:text-white p-1"
                        onClick={() => removeImage(index)}
                      >
                        <X className="h-3 w-3" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Exercise Overview */}
        <Card className={`${theme.colors.secondary} ${theme.colors.border}`}>
          <CardHeader>
            <CardTitle className="text-orange-500">Exercise Overview</CardTitle>
          </CardHeader>
          <CardContent>
            <Textarea
              value={formData.exercise_overview}
              onChange={(e) => handleInputChange('exercise_overview', e.target.value)}
              placeholder="Provide a brief overview of the exercise, its purpose, and scope..."
              className={`${theme.colors.input} min-h-[100px]`}
            />
          </CardContent>
        </Card>

        {/* Key Areas Assessment */}
        <Card className={`${theme.colors.secondary} ${theme.colors.border}`}>
          <CardHeader>
            <CardTitle className="text-orange-500">Key Areas Assessment</CardTitle>
            <CardDescription className={theme.colors.textMuted}>
              Evaluate critical operational areas during the exercise
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {[
              { key: 'command_and_control', title: 'Command and Control', description: 'Leadership, decision-making processes, and clarity of roles and responsibilities' },
              { key: 'operations', title: 'Operations', description: 'Tactical execution of the incident action plan; coordination of the response effort in the field' },
              { key: 'planning', title: 'Planning', description: 'Collection, evaluation, and dissemination of incident information; development of the Incident Action Plan' },
              { key: 'logistics', title: 'Logistics', description: 'Provision of facilities, services, personnel, equipment, and materials to support the incident' },
              { key: 'finance_admin', title: 'Finance / Admin', description: 'Cost tracking, time recording, procurement, compensation/claims, and administrative support' },
              { key: 'communication', title: 'Communication', description: 'Internal and external communication, information dissemination to relevant parties and emergency services' },
              { key: 'resource_management', title: 'Resource Management', description: 'Availability, allocation, and effectiveness of personnel, equipment, and supplies' },
              { key: 'safety_and_security', title: 'Safety and Security', description: 'Personnel protection, first aid, hazardous materials management, and overall security' },
              { key: 'training_and_readiness', title: 'Training and Readiness', description: 'Staff training adequacy and preparedness to carry out assigned roles' }
            ].map((area) => (
              <div key={area.key} className={`${theme.colors.tertiary || 'bg-gray-800'} p-4 rounded border ${theme.colors.border}`}>
                <h4 className={`text-lg font-semibold ${theme.colors.textPrimary} mb-2`}>{area.title}</h4>
                <p className={`text-sm ${theme.colors.textMuted} mb-3`}>{area.description}</p>
                <div className="space-y-4">
                  <div>
                    <Label className={`${theme.colors.textPrimary} block mb-2`}>Rating</Label>
                    <div className="flex flex-wrap items-center gap-x-6 gap-y-2" role="radiogroup" aria-label={`${area.title} rating`}>
                      {ratingOptions.map((option) => {
                        const selected = (formData[area.key]?.rating || 'Average') === option;
                        const palette = {
                          'Excellent':     { dot: 'bg-blue-500',   ring: 'border-blue-500',   text: 'text-blue-400'   },
                          'Above Average': { dot: 'bg-sky-400',    ring: 'border-sky-400',    text: 'text-sky-400'    },
                          'Average':       { dot: 'bg-green-500',  ring: 'border-green-500',  text: 'text-green-400'  },
                          'Below Average': { dot: 'bg-orange-500', ring: 'border-orange-500', text: 'text-orange-400' },
                          'Unacceptable':  { dot: 'bg-red-500',    ring: 'border-red-500',    text: 'text-red-400'    },
                          'Not Assessed':  { dot: 'bg-gray-400',   ring: 'border-gray-400',   text: 'text-gray-300'   },
                        }[option];
                        return (
                          <button
                            type="button"
                            key={option}
                            role="radio"
                            aria-checked={selected}
                            onClick={() => handleAreaAssessmentChange(area.key, 'rating', option)}
                            data-testid={`eval-rating-${area.key}-${option.toLowerCase().replace(/\s+/g,'-')}`}
                            className={`inline-flex items-center gap-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-500 rounded transition-opacity ${selected ? 'opacity-100' : 'opacity-70 hover:opacity-100'}`}
                          >
                            <span className={`h-5 w-5 rounded-full border-2 ${palette.ring} flex items-center justify-center`}>
                              {selected && <span className={`h-3 w-3 rounded-full ${palette.dot}`} />}
                            </span>
                            <span className={`text-sm ${selected ? `${palette.text} font-semibold` : theme.colors.textSecondary}`}>{option}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  <div>
                    <Label className={theme.colors.textPrimary}>Comments</Label>
                    <Textarea
                      value={formData[area.key]?.comments || ''}
                      onChange={(e) => handleAreaAssessmentChange(area.key, 'comments', e.target.value)}
                      placeholder="Assessment comments..."
                      rows={2}
                      className={theme.colors.input}
                      data-testid={`eval-comments-${area.key}`}
                    />
                  </div>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Summary and Findings */}
        <Card className={`${theme.colors.secondary} ${theme.colors.border}`}>
          <CardHeader>
            <CardTitle className="text-orange-500">Summary and Findings</CardTitle>
            <CardDescription className={theme.colors.textMuted}>
              Strengths and Areas for Improvement
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className={`${theme.colors.tertiary || 'bg-gray-800'} p-4 rounded border ${theme.colors.border}`}>
              <Label className={`${theme.colors.textPrimary} text-lg font-semibold`}>Overall Rating</Label>
              <div className="mt-2">
                <Badge 
                  variant="outline" 
                  className={`text-lg px-4 py-2 ${
                    calculateOverallRating() === 'Excellent' ? 'border-blue-500 text-blue-400 bg-blue-500/10' :
                    calculateOverallRating() === 'Above Average' ? 'border-sky-400 text-sky-400 bg-sky-400/10' :
                    calculateOverallRating() === 'Average' ? 'border-green-500 text-green-400 bg-green-500/10' :
                    calculateOverallRating() === 'Below Average' ? 'border-orange-500 text-orange-400 bg-orange-500/10' :
                    calculateOverallRating() === 'Not Assessed' ? 'border-gray-400 text-gray-300 bg-gray-500/10 italic' :
                    'border-red-500 text-red-400 bg-red-500/10'
                  }`}
                >
                  Overall Rating: {calculateOverallRating()}
                </Badge>
              </div>
              <p className={`text-sm ${theme.colors.textMuted} mt-2`}>
                Calculated automatically as the average of the assessment areas that have been rated. Areas marked "Not Assessed" are excluded.
              </p>
            </div>

            <div>
              <Label className={theme.colors.textPrimary}>Summary of Findings</Label>
              <Textarea
                value={formData.summary_of_findings}
                onChange={(e) => handleInputChange('summary_of_findings', e.target.value)}
                placeholder="Provide a high-level overview of what went well and what needs improvement..."
                rows={3}
                className={theme.colors.input}
              />
            </div>

            <div>
              <Label className={theme.colors.textPrimary}>Strengths</Label>
              <Textarea
                value={formData.strengths}
                onChange={(e) => handleInputChange('strengths', e.target.value)}
                placeholder="Detail what worked effectively during the exercise..."
                rows={3}
                className={theme.colors.input}
              />
            </div>

            <div>
              <Label className={theme.colors.textPrimary}>Areas for Improvement</Label>
              <Textarea
                value={formData.areas_for_improvement}
                onChange={(e) => handleInputChange('areas_for_improvement', e.target.value)}
                placeholder="List the aspects of the plan, procedures, or response that were deficient..."
                rows={3}
                className={theme.colors.input}
              />
            </div>

            <div>
              <Label className={theme.colors.textPrimary}>Key Findings (Narrative)</Label>
              <Textarea
                value={formData.key_findings_narrative}
                onChange={(e) => handleInputChange('key_findings_narrative', e.target.value)}
                placeholder="Include detailed, objective narrative summaries of specific events, problems, or successes observed..."
                rows={4}
                className={theme.colors.input}
              />
            </div>
          </CardContent>
        </Card>

        {/* Recommendations and Appendices */}
        <Card className={`${theme.colors.secondary} ${theme.colors.border}`}>
          <CardHeader>
            <CardTitle className="text-orange-500">Recommendations and Appendices</CardTitle>
            <CardDescription className={theme.colors.textMuted}>
              Action items and supporting documentation
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label className={theme.colors.textPrimary}>Recommendations</Label>
              <Textarea
                value={formData.recommendations}
                onChange={(e) => handleInputChange('recommendations', e.target.value)}
                placeholder="Assign responsibilities and timelines for addressing identified gaps..."
                rows={4}
                className={theme.colors.input}
              />
            </div>
            <div>
              <Label className={theme.colors.textPrimary}>Appendices</Label>
              <p className={`${theme.colors.textMuted} text-xs mt-1 mb-2`}>
                Upload supporting documents (Word, Excel, PDF, PowerPoint — max 10 MB each)
              </p>

              <div className="flex items-center gap-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => document.getElementById('appendix-file-input')?.click()}
                  className="border-orange-500 text-orange-500 hover:bg-orange-500 hover:text-black"
                  data-testid="appendix-upload-btn"
                >
                  <Upload className="h-4 w-4 mr-2" />
                  Choose Files
                </Button>
                <input
                  id="appendix-file-input"
                  type="file"
                  multiple
                  accept=".doc,.docx,.xls,.xlsx,.pdf,.ppt,.pptx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation"
                  onChange={handleAppendixUpload}
                  className="hidden"
                  data-testid="appendix-file-input"
                />
                <span className={`${theme.colors.textMuted} text-xs`}>
                  Accepted: .doc · .docx · .xls · .xlsx · .pdf · .ppt · .pptx
                </span>
              </div>

              {formData.appendix_files && formData.appendix_files.length > 0 && (
                <div className="mt-3 space-y-2" data-testid="appendix-files-list">
                  {formData.appendix_files.map((file, idx) => {
                    const Icon = getFileIcon(file.name);
                    return (
                      <div
                        key={file.id || idx}
                        className={`flex items-center justify-between rounded border ${theme.colors.border} bg-gray-800/40 px-3 py-2`}
                        data-testid={`appendix-file-${idx}`}
                      >
                        <button
                          type="button"
                          onClick={() => downloadAppendixFile(file)}
                          className={`flex items-center gap-3 text-left flex-1 min-w-0 ${theme.colors.textPrimary} hover:text-orange-400`}
                          title="Click to download"
                        >
                          <Icon className="h-5 w-5 text-orange-400 flex-shrink-0" />
                          <div className="min-w-0">
                            <div className="text-sm truncate">{file.name}</div>
                            <div className={`${theme.colors.textMuted} text-xs`}>{formatFileSize(file.size)}</div>
                          </div>
                        </button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removeAppendixFile(idx)}
                          className="text-red-400 hover:text-red-300 hover:bg-red-500/10 flex-shrink-0"
                          data-testid={`appendix-remove-${idx}`}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Spacer for sticky footer */}
        <div className="h-20"></div>
      </form>

      {/* Sticky Submit Buttons */}
      <div className="sticky bottom-0 left-0 right-0 bg-gray-900/95 backdrop-blur-sm border-t border-gray-700 p-4 flex justify-end space-x-4 z-10">
        <Button
          type="button"
          variant="outline"
          onClick={onBack}
          className="border-gray-600 text-gray-400 hover:bg-gray-700"
        >
          Cancel
        </Button>
        <Button
          type="submit"
          form="evaluation-report-form"
          disabled={loading}
          className="bg-orange-500 hover:bg-orange-600 text-black font-semibold px-8 py-2 text-base"
          data-testid="eval-form-submit-btn"
        >
          {loading ? 'Saving...' : (editingReport ? 'Update Report' : 'Create Report')}
        </Button>
      </div>
    </div>
  );
};

export default EvaluationReportForm;
